import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/client';
import api from '../api/client';

const AuthContext = createContext(null);

// Bootstrap retry schedule (ms) — generous enough to survive a backend
// cold start. Only a definitive 401/403 gives up earlier.
const RETRY_DELAYS = [2000, 5000, 10000, 20000];
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('kup-token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    let cancelled = false;
    (async () => {
      const authHeaders = { Authorization: `Bearer ${token}` };
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        try {
          // Long per-request timeout: a sleeping backend can take ~1 min to wake.
          const res = await authApi.get('/me', { headers: authHeaders, timeout: 60000 });
          if (cancelled) return;
          setUser(res.data);
          setLoading(false);
          // Sliding session: swap in a fresh token so active users never expire.
          try {
            const ref = await authApi.post('/refresh', null, { headers: authHeaders, timeout: 60000 });
            if (!cancelled && ref.data?.token) {
              localStorage.setItem('kup-token', ref.data.token);
              api.defaults.headers.common['Authorization'] = `Bearer ${ref.data.token}`;
            }
          } catch { /* refresh is best-effort */ }
          return;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            // The token is genuinely invalid/expired — only then log out.
            if (!cancelled) {
              localStorage.removeItem('kup-token');
              delete api.defaults.headers.common['Authorization'];
              setLoading(false);
            }
            return;
          }
          // Network error / timeout / 5xx: keep the token and retry.
          if (attempt < RETRY_DELAYS.length) await sleep(RETRY_DELAYS[attempt]);
        }
      }
      // Retries exhausted without a definitive rejection — keep the token for
      // next load, but show the login screen for now.
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, []);

  // Mid-session 401 (token expired/revoked while using the app): log out once
  // cleanly instead of leaving broken pages.
  useEffect(() => {
    const id = api.interceptors.response.use(
      res => res,
      err => {
        if (err?.response?.status === 401 && localStorage.getItem('kup-token')) {
          localStorage.removeItem('kup-token');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (username, password) => {
    const res = await authApi.post('/login', { username, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('kup-token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('kup-token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const hasPermission = (key) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.permissions?.[key] === true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
